/**
 * toFilterConfig (BAL-664, layer 2). Derives the shared `ListingFilterConfig`
 * (consumed by <ListingFilterBar> / <ListingFilterSheet>) from a registry.
 *
 * The registry holds i18n KEYS + column names; the UI config wants PRE-TRANSLATED
 * labels and no DB internals. So this runs in a translation context (takes `t`)
 * and strips every registry-only field (column, predicate, orderBy, seoSegment,
 * allowList). Dynamic option sets (cities, brands) are injected via
 * `dynamicOptions` — the registry needn't hold them.
 */
import type { FilterFacet, ListingFilterConfig } from '~/components/listing-filters/types';

import type { ListingRegistry } from './define-listing';

type Translate = (key: string, defaultValue?: string) => string;

export interface ToFilterConfigOptions {
  /** i18n key for the search placeholder (translated here). */
  searchPlaceholderKey?: string;
  /** Enable the "magic"/AI search affordance. */
  magicSearch?: boolean;
  /** Injected option sets for facets whose values are dynamic (facetKey → options). */
  dynamicOptions?: Record<string, { value: string; label: string }[]>;
}

export function toFilterConfig<Row>(
  registry: ListingRegistry<Row>,
  t: Translate,
  options: ToFilterConfigOptions = {},
): ListingFilterConfig {
  const facets: FilterFacet[] = registry.facets.map((facet) => {
    if (facet.kind === 'range') {
      return {
        kind: 'range',
        key: facet.key,
        label: t(`${registry.table}:facets.${facet.key}`),
        min: facet.min ?? 0,
        max: facet.max ?? 0,
        unit: facet.unit,
      };
    }

    const injected = options.dynamicOptions?.[facet.key];
    const staticOptions =
      facet.options?.map((o) => ({ value: o.value, label: t(o.labelKey) })) ??
      [];

    return {
      kind: facet.kind,
      key: facet.key,
      label: t(`${registry.table}:facets.${facet.key}`),
      options: injected ?? staticOptions,
      multi: facet.multi,
    };
  });

  return {
    search: registry.search
      ? {
          placeholder: options.searchPlaceholderKey
            ? t(options.searchPlaceholderKey)
            : t(`${registry.table}:searchPlaceholder`, 'Search…'),
          magic: options.magicSearch,
        }
      : undefined,
    facets,
    sorts: registry.sorts.map((s) => ({ value: s.value, label: t(s.labelKey) })),
    defaultSort: registry.defaultSort,
  };
}
